import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AspectRatio } from '@src/types.ts/video';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';
import { LogManager } from '@src/utility/Logger';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { getRandomFileName } from '../../utility/general';
import Modal from '../common/Modal';
import PhotoModalTop from './PhotoModalTop';
import PhotoModal from './PhotosModal';
import { PickerModalCard } from './pickerModalStyles';
import { getReplaceMediaTitle } from './replaceMediaTitle';

import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type { FileData } from '../../types.ts/story';
import type { PhotoModalOtherFields } from './PhotosModal';

const logger = LogManager.getLogger('component:ReplacePhotoElementModal');

type Props = {
  title?: string;
  origin?: string;
};

const ReplacePhotoElementModal = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const [isReplacing, setIsReplacing] = useState(false);

  const origin =
    props.origin ||
    videoCreator.openPhotoElementReplacementModal?.origin ||
    'transcript';

  const element = videoCreator.openPhotoElementReplacementModal?.element;
  const parentCompositionElement =
    videoCreator.openPhotoElementReplacementModal?.parentCompositionElement;
  const isLogoControl =
    videoCreator.openPhotoElementReplacementModal?.isLogoControl;
  const isVideoControl =
    videoCreator.openPhotoElementReplacementModal?.isVideoControl;
  const isHeadshotControl =
    videoCreator.openPhotoElementReplacementModal?.isHeadshotControl;

  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);

  // Names the subject being replaced. `isLogoElement` covers a logo picked on
  // the canvas, where no control flag is set.
  const title =
    props.title ||
    videoCreator.openPhotoElementReplacementModal?.title ||
    getReplaceMediaTitle({
      isLogo:
        isLogoControl || (!!element && videoCreator.isLogoElement(element)),
      isVideo: isVideoControl,
      isHeadshot: isHeadshotControl,
    });

  const handleReplacePhoto = async () => {
    if (!selectedImage?.url || !element) return;

    try {
      let imageUrl = applyImageOptimizations(selectedImage.url);
      let uploadId = selectedImage.id;

      if (selectedImage.type === 'ai') {
        setIsReplacing(true);
        const altText = selectedImage.alt || 'AI generated image';
        const fileName = getRandomFileName(altText);
        const newPhotoData: FileData & { fileName: string } = {
          type: 'ai',
          url: selectedImage.url,
          fileName,
          alt: altText.slice(0, 150),
          title: altText.slice(0, 150),
        };
        const newUpload =
          await datoClientStore.assetRepository?.uploadFile(newPhotoData);
        if (newUpload) {
          imageUrl = newUpload.url;
          uploadId = newUpload.id;
          runInAction(() => {
            videoCreator.photoDataForDato = {
              ...videoCreator.photoDataForDato,
              [element.source.id]: {
                type: selectedImage.type,
                url: imageUrl,
                uploadId,
              },
            };
          });
        }
      }

      const isInsideComposition = !!parentCompositionElement;
      const isLogo = isLogoControl || videoCreator.isLogoElement(element);

      videoCreator.openPhotoElementReplacementModal?.onImageSourceChange?.(
        imageUrl,
        uploadId,
      );
      if (
        videoCreator.openPhotoElementReplacementModal?.callbackReplacesElement
      ) {
        // The template replaced the element; stay on the current sidebar tab.
      } else if (isInsideComposition) {
        // Simplified path: image is a child inside a composition. Only update the image source.
        // Do NOT alter aspect ratio, type, elements array, or perform portrait composition reconstruction.

        // Logos should fit within their frame rather than fill/crop it.
        if (isLogo) {
          videoCreator.rootStore.editorStore.editor.commandUpdateElement(
            element.source.id,
            { fit: 'contain' },
          );
        }

        await videoCreator.setActiveElements(
          parentCompositionElement.source.id,
        );
      } else {
        // Original logic for standalone image elements.
        const id = element.source.id;
        const isPortrait = +selectedImage.width < +selectedImage.height;
        const videoAspectRatio =
          videoCreator.currentVideo?.aspectRatio ||
          videoCreator.getDefaultAspectRatio();

        // Build flat property updates for the element.
        // Pass fit: 'black-frame' for portrait images so that
        // UpdateElementCommand.detectTypeTransition routes to
        // ElementTypeTransitionCommand, which creates a proper
        // CompositionElement (ImageElement → CompositionElement class swap).
        const propertyUpdates: Record<string, unknown> =
          !isLogo && isPortrait && videoAspectRatio === AspectRatio.AR_16_9
            ? {
                fit: 'black-frame',
                source: imageUrl,
              }
            : {
                type: 'image',
                source: imageUrl,
                // Logos should fit within their frame rather than fill/crop it.
                fit: isLogo ? 'contain' : 'cover',
                smart_crop: true,
                elements: [],
              };

        if (
          !videoCreator.hasLandscapeAspectRatio() &&
          propertyUpdates.fit === 'cover'
        ) {
          // adjustModificationsToAspectRatio uses dot-path keys; build a temporary
          // dict then extract the flat properties back out.
          const dotPathMods: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(propertyUpdates)) {
            dotPathMods[`${id}.${key}`] = value;
          }
          await videoCreator.adjustModificationsToAspectRatio(dotPathMods, {
            ...videoCreator.getElementSource(element),
            fit: 'cover',
            type: 'image',
            source: imageUrl,
            arbor_naturalDimensions: {
              width: +selectedImage.width,
              height: +selectedImage.height,
            },
          });
          // Extract flat properties back from dot-path modifications
          for (const [key, value] of Object.entries(dotPathMods)) {
            const prop = key.replace(`${id}.`, '');
            propertyUpdates[prop] = value;
          }
        }

        const editorStore = videoCreator.rootStore.editorStore;
        editorStore.editor.commandUpdateElement(id, propertyUpdates);
        await videoCreator.setActiveElements(element.source.id);

        // Replace item in punchlist, if it exists.
        if (videoCreator.punchListManager.length()) {
          const punchListItem =
            videoCreator.punchListManager.findPunchListItemById(
              element.source.id,
            );
          if (punchListItem) {
            runInAction(() => {
              videoCreator.punchListManager.updatePunchListItem(
                element.source.id,
                {
                  artifactSrc: imageUrl,
                  artifactAspectRatio:
                    +selectedImage.width / +selectedImage.height,
                },
              );
            });
          }
        }
      }
    } catch (error) {
      logger.error('Error replacing photo', { error });
    } finally {
      setIsReplacing(false);
      videoCreator.openPhotoElementReplacementModal = null;
    }
  };

  // if (!element || !videoCreator.isImageElement(element)) return null;
  if (
    !element ||
    (!videoCreator.isImageElement(element) &&
      !videoCreator.isVideoElement(element))
  )
    return null;

  const getOtherFields = (): PhotoModalOtherFields[] => {
    if (isHeadshotControl) {
      return ['headshots', 'org_photos', 'stock'];
    } else if (videoCreator.isLogoElement(element) || isLogoControl) {
      return ['org_logos'];
    } else if (isVideoControl) {
      return ['org_videos'];
    } else {
      return ['ai', 'stock', 'org_photos'];
    }
  };

  return (
    <Modal
      isOpen={true}
      closeModal={() => (videoCreator.openPhotoElementReplacementModal = null)}
    >
      <PickerModalCard>
        <PhotoModal
          TopComponent={props => (
            <PhotoModalTop
              {...props}
              replaceAction={handleReplacePhoto}
              title={title}
              isSelected={!!selectedImage}
              selectedImage={selectedImage}
              isLoading={isReplacing}
              setSelectedImage={setSelectedImage}
            />
          )}
          showDescription
          otherFields={getOtherFields()}
          onCloseSelf={() =>
            (videoCreator.openPhotoElementReplacementModal = null)
          }
          selectedImageUrl={selectedImage?.url || null}
          setSelectedImage={setSelectedImage}
          origin={origin}
        />
      </PickerModalCard>
    </Modal>
  );
});

export default ReplacePhotoElementModal;
